import type { ZodType, ZodTypeDef } from 'zod';

/** Prefixo da API na mesma origem da interface (o proxy/Caddy remove o prefixo antes do servidor). */
export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

/** Erro de uma chamada à API, já com mensagem pronta para mostrar ao usuário (pt-BR). */
export class ApiError extends Error {
  /** Status HTTP; 0 = não houve resposta (rede fora do ar, servidor inacessível). */
  readonly status: number;
  /** Código estável da API (`invalid_credentials`, `not_found`…) ou `network`/`bad_response`. */
  readonly code: string;
  /** Segundos a esperar, quando o servidor pediu (`Retry-After`, ex.: excesso de tentativas). */
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    code: string,
    message: string,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Tempo limite padrão de um pedido: sem ele um servidor que trava deixa a tela "carregando" para sempre. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Texto mostrado quando o servidor devolve `invalid_csrf` (o texto cru dele não diz o que fazer). */
export const SESSION_CHANGED_MESSAGE = 'Sua sessão mudou em outra aba. Tente de novo.';

export const isApiError = (err: unknown): err is ApiError => err instanceof ApiError;

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions<T> {
  method?: Method;
  query?: Record<string, QueryValue>;
  body?: unknown;
  /**
   * Valida e tipa a resposta; sem schema a resposta deve ser vazia (204). O tipo de ENTRADA é livre
   * (`unknown`): um schema com `.default(...)` aceita um campo ausente (entrada) e o entrega
   * preenchido (saída), e é a saída que o resto do app enxerga.
   */
  schema?: ZodType<T, ZodTypeDef, unknown>;
  signal?: AbortSignal;
  /**
   * Tempo máximo (ms) para o pedido INTEIRO: resposta e leitura do corpo. Passado esse prazo o pedido
   * é abortado e sobe `ApiError(0, 'timeout')`. O padrão é {@link DEFAULT_TIMEOUT_MS}; `Infinity`
   * desliga o limite (`setTimeout` não aceita `Infinity`: viraria disparo imediato).
   */
  timeoutMs?: number;
  /**
   * `public`: um 401 aqui NÃO significa "sessão expirada" (login, "quem sou eu").
   * `session` (padrão): um 401 avisa o ouvinte de sessão expirada.
   */
  auth?: 'session' | 'public';
}

// ---------------------------------------------------------------- estado de sessão do cliente

let csrfToken: string | null = null;
/**
 * "Geração" da sessão desta aba: sobe sempre que o token CSRF muda (login, logout, troca de conta
 * vista por outra aba). Um 401 que chega para um pedido iniciado numa geração ANTERIOR é de uma
 * sessão que já acabou e não pode derrubar a atual (ex.: um envio lento da conta A que responde
 * 401 depois de a conta B entrar na mesma aba).
 */
let sessionEpoch = 0;
let unauthorizedHandler: (() => void) | null = null;
let invalidCsrfHandler: (() => void) | null = null;

/** Token CSRF da sessão atual: enviado em todo pedido que altera dados. Vive só em memória. */
export function setCsrfToken(token: string | null): void {
  if (token !== csrfToken) sessionEpoch += 1;
  csrfToken = token;
}

/** Chamado quando uma rota protegida responde 401 (a sessão acabou no meio do uso). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * Chamado quando o servidor recusa o token CSRF (403 `invalid_csrf`): o cookie de sessão do
 * navegador, que as abas compartilham, passou a ser de outra sessão (login ou troca de conta em
 * outra aba). Quem registra reconcilia a identidade e o token desta aba com o servidor.
 */
export function setInvalidCsrfHandler(handler: (() => void) | null): void {
  invalidCsrfHandler = handler;
}

/** Para testes: volta ao estado inicial. */
export function resetApiState(): void {
  csrfToken = null;
  unauthorizedHandler = null;
  invalidCsrfHandler = null;
}

// ---------------------------------------------------------------- núcleo

const MUTATING = new Set<Method>(['POST', 'PUT', 'PATCH', 'DELETE']);

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = new URL(API_BASE + path, window.location.href);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function genericMessage(status: number): string {
  if (status === 401) return 'Sua sessão expirou. Entre novamente.';
  if (status === 403) return 'Você não tem permissão para fazer isso.';
  if (status === 404) return 'Não encontrado.';
  if (status === 429) return 'Muitas tentativas. Aguarde um pouco e tente de novo.';
  if (status >= 500) return 'O servidor está indisponível no momento. Tente de novo em instantes.';
  return 'Não foi possível concluir a operação.';
}

function parseRetryAfter(res: Response): number | null {
  const raw = res.headers.get('retry-after');
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'http_error';
  let message = genericMessage(res.status);
  try {
    const body: unknown = await res.json();
    const error = (body as { error?: { code?: unknown; message?: unknown } } | null)?.error;
    if (typeof error?.code === 'string') code = error.code;
    if (typeof error?.message === 'string' && error.message) message = error.message;
  } catch {
    /* corpo que não é JSON (ex.: página de erro do proxy): vale a mensagem genérica */
  }
  return new ApiError(res.status, code, message, parseRetryAfter(res));
}

/**
 * O erro a lançar para uma resposta que não é de sucesso, depois dos efeitos que ela pede: um 401 avisa
 * que a sessão acabou (só se for a mesma de quando o pedido saiu, ver `sessionEpoch`) e um
 * `invalid_csrf` manda reconciliar a sessão (sem repetir o pedido: se a conta mudou, repeti-lo agiria
 * como OUTRA pessoa; quem decide é o usuário).
 */
async function failureOf(
  res: Response,
  epoch: number,
  auth: RequestOptions<unknown>['auth'],
): Promise<ApiError> {
  const error = await toApiError(res);
  if (res.status === 401 && auth !== 'public' && epoch === sessionEpoch) unauthorizedHandler?.();
  if (res.status === 403 && error.code === 'invalid_csrf') {
    // Vale para qualquer pedido, até os "públicos" (o logout manda o token).
    invalidCsrfHandler?.();
    return new ApiError(403, 'invalid_csrf', SESSION_CHANGED_MESSAGE, error.retryAfterSeconds);
  }
  return error;
}

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

const timeoutError = (): ApiError =>
  new ApiError(0, 'timeout', 'A conexão demorou demais. Tente de novo.');

export async function request<T = void>(path: string, opts: RequestOptions<T> = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const epoch = sessionEpoch;
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (MUTATING.has(method) && csrfToken) headers['x-csrf-token'] = csrfToken;

  // Sinal próprio, ligado ao do chamador: é ele que o tempo limite aborta. `timedOut` é quem
  // distingue "demorou demais" de "o chamador cancelou" (os dois abortam o mesmo sinal).
  const controller = new AbortController();
  const { signal: callerSignal } = opts;
  let timedOut = false;
  const onCallerAbort = (): void => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    // `abort` não dispara para um sinal que já nasceu cancelado: o fetch precisa saber disso já.
    if (callerSignal.aborted) onCallerAbort();
    else callerSignal.addEventListener('abort', onCallerAbort);
  }
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = Number.isFinite(timeoutMs)
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs)
    : undefined;

  try {
    let res: Response;
    try {
      res = await fetch(buildUrl(path, opts.query), {
        method,
        headers,
        credentials: 'same-origin',
        signal: controller.signal,
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      });
    } catch (err) {
      if (timedOut) throw timeoutError();
      // Cancelamento é fluxo normal (a tela mudou): quem chamou decide, não vira "erro de rede".
      if (isAbortError(err)) throw err;
      throw new ApiError(
        0,
        'network',
        'Sem conexão com o servidor. Verifique sua internet e tente de novo.',
      );
    }

    if (!res.ok) throw await failureOf(res, epoch, opts.auth);

    if (!opts.schema) return undefined as T;

    let json: unknown;
    try {
      json = await res.json();
    } catch (err) {
      // O mesmo sinal vale para a leitura do corpo: estourar o prazo aqui também é "timeout".
      if (timedOut) throw timeoutError();
      if (isAbortError(err)) throw err;
      throw new ApiError(res.status, 'bad_response', 'O servidor enviou uma resposta inesperada.');
    }
    const parsed = opts.schema.safeParse(json);
    if (!parsed.success) {
      // Só os caminhos dos campos (nunca os valores, que podem ter dados de clientes).
      console.error(
        'Resposta fora do contrato:',
        path,
        parsed.error.issues.map((i) => i.path.join('.')),
      );
      throw new ApiError(res.status, 'bad_response', 'O servidor enviou uma resposta inesperada.');
    }
    return parsed.data;
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', onCallerAbort);
  }
}

// ---------------------------------------------------------------- envio de arquivo

/** Tempo máximo de um envio de arquivo: até 100 MB numa conexão lenta. */
export const UPLOAD_TIMEOUT_MS = 10 * 60_000;

export interface UploadOptions<T> {
  query?: Record<string, QueryValue>;
  /** O arquivo vai como corpo do pedido (não é multipart). */
  file: Blob;
  /** `Content-Type` do pedido: o tipo do arquivo. */
  contentType: string;
  schema: ZodType<T, ZodTypeDef, unknown>;
  signal?: AbortSignal;
  /** De 0 a 1, enquanto o arquivo sobe. */
  onProgress?: (fraction: number) => void;
  timeoutMs?: number;
}

/**
 * Envia um arquivo (POST com o arquivo como corpo). Usa XMLHttpRequest porque o `fetch` não informa o
 * progresso do envio. Mesmas regras do `request`: token CSRF, sessão expirada, erros com mensagem em
 * português e cancelamento por `signal` (sobe `AbortError`).
 */
export function uploadFile<T>(path: string, opts: UploadOptions<T>): Promise<T> {
  const epoch = sessionEpoch;
  return new Promise<T>((resolve, reject) => {
    const callerSignal = opts.signal;
    if (callerSignal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildUrl(path, opts.query));
    xhr.timeout = opts.timeoutMs ?? UPLOAD_TIMEOUT_MS;
    xhr.setRequestHeader('accept', 'application/json');
    xhr.setRequestHeader('content-type', opts.contentType);
    if (csrfToken) xhr.setRequestHeader('x-csrf-token', csrfToken);

    const onCallerAbort = (): void => xhr.abort();
    callerSignal?.addEventListener('abort', onCallerAbort);
    const done = (): void => callerSignal?.removeEventListener('abort', onCallerAbort);

    if (opts.onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0)
          opts.onProgress?.(event.loaded / event.total);
      };
    }
    xhr.onerror = () => {
      done();
      reject(
        new ApiError(
          0,
          'network',
          'Sem conexão com o servidor. Verifique sua internet e tente de novo.',
        ),
      );
    };
    xhr.ontimeout = () => {
      done();
      reject(timeoutError());
    };
    xhr.onabort = () => {
      done();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    xhr.onload = () => {
      done();
      void (async () => {
        const retryAfter = xhr.getResponseHeader('retry-after');
        const res = new Response(xhr.status === 204 ? null : xhr.responseText, {
          status: xhr.status,
          ...(retryAfter ? { headers: { 'retry-after': retryAfter } } : {}),
        });
        if (!res.ok) throw await failureOf(res, epoch, 'session');
        let json: unknown;
        try {
          json = JSON.parse(xhr.responseText);
        } catch {
          throw new ApiError(
            xhr.status,
            'bad_response',
            'O servidor enviou uma resposta inesperada.',
          );
        }
        const parsed = opts.schema.safeParse(json);
        if (!parsed.success) {
          console.error(
            'Resposta fora do contrato:',
            path,
            parsed.error.issues.map((i) => i.path.join('.')),
          );
          throw new ApiError(
            xhr.status,
            'bad_response',
            'O servidor enviou uma resposta inesperada.',
          );
        }
        return parsed.data;
      })().then(resolve, reject);
    };
    opts.onProgress?.(0);
    xhr.send(opts.file);
  });
}
